import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Form from '../../../Forms/form';
import { FormGroup, FormTypeEnum } from '../../../Forms/formInterfaces';
import RequiredFieldsNote from '../../../Forms/RequiredFieldsNote';
import { KPI_TYPE } from '../../../HotAlerts/hotAlertsInterface';
import SimpleActionButton from '../../ActionButton/simpleActionButton';
import { ConfiguratedHotAlerts, HotAlertStatusConfigItem, SelectedQuestion } from '../addSurveyInterface';
import HotAlertStatusEditor from './HotAlertStatusEditor';
import { StepConfigureFooterStyled } from './styledStepConfigure';

interface StepConfigureHotAlertsProps {
    selectedQuestions: SelectedQuestion[];
    handleNextStep: (formId: string) => void;
    onConfigureHotAlerts: (config: ConfiguratedHotAlerts) => void;
    configuratedHotAlerts?: ConfiguratedHotAlerts;
}

const StepConfigureHotAlerts: FC<StepConfigureHotAlertsProps> = ({ selectedQuestions, handleNextStep, onConfigureHotAlerts, configuratedHotAlerts: initialConfig }) => {
    const { t } = useTranslation('settings');
    const [openedQuestion, setOpenedQuestion] = useState<string | undefined>(initialConfig?.openedQuestion !== undefined ? String(initialConfig.openedQuestion) : undefined);
    const [firstName, setFirstName] = useState<string | undefined>(initialConfig?.firstName !== undefined ? String(initialConfig.firstName) : undefined);
    const [lastName, setLastName] = useState<string | undefined>(initialConfig?.lastName !== undefined ? String(initialConfig.lastName) : undefined);
    const [phone, setPhone] = useState<string | undefined>(initialConfig?.phone !== undefined ? String(initialConfig.phone) : undefined);
    const [mail, setMail] = useState<string | undefined>(initialConfig?.mail !== undefined ? String(initialConfig.mail) : undefined);
    const [satisfaction, setSatisfaction] = useState<string | undefined>(initialConfig?.satisfaction !== undefined ? String(initialConfig.satisfaction.questionId) : undefined);
    const [satisfactionPositiveModalities, setSatisfactionPositiveModalities] = useState<string[]>(initialConfig?.satisfaction?.positiveModalityIds.map(String) ?? []);
    const [satisfactionNegativeModalities, setSatisfactionNegativeModalities] = useState<string[]>(initialConfig?.satisfaction?.negativeModalityIds.map(String) ?? []);
    const [additionalQuestions, setAdditionalQuestions] = useState<string[]>(initialConfig?.additionalQuestion.map(String) ?? []);
    const [detailQuestions, setDetailQuestions] = useState<string[]>(initialConfig?.detailQuestions.map(String) ?? []);
    const [statusConfigs, setStatusConfigs] = useState<HotAlertStatusConfigItem[]>(initialConfig?.statuses ?? [
        { tempId: 'default-1', label: t('tabs.createSurvey.step5Content.statusDefaultLabel1'), isDefault: true, isFinal: false, colors: { color: '#E5EAF1', fontColor: '#8993A2' } },
        { tempId: 'default-2', label: t('tabs.createSurvey.step5Content.statusDefaultLabel2'), isDefault: false, isFinal: false, colors: { color: '#F97215', fontColor: '#FFFFFF' } },
        { tempId: 'default-3', label: t('tabs.createSurvey.step5Content.statusDefaultLabel3'), isDefault: false, isFinal: true, colors: { color: '#3CCC71', fontColor: '#FFFFFF' } },
    ]);

    const handleChangeOpenedQuestion = (choice: { id: string; label: string }) => setOpenedQuestion(choice.id);
    const handleChangeFirstName = (choice: { id: string; label: string }) => setFirstName(choice.id);
    const handleChangeLastName = (choice: { id: string; label: string }) => setLastName(choice.id);
    const handleChangePhone = (choice: { id: string; label: string }) => setPhone(choice.id);
    const handleChangeMail = (choice: { id: string; label: string }) => setMail(choice.id);
    const handleChangeSatisfaction = (choice: { id: string; label: string }) => {
        setSatisfaction(choice.id);
        setSatisfactionPositiveModalities([]);
        setSatisfactionNegativeModalities([]);
    };
    const handleChangeSatisfactionPositiveModalities = (ids: string[]) => setSatisfactionPositiveModalities(ids);
    const handleChangeSatisfactionNegativeModalities = (ids: string[]) => setSatisfactionNegativeModalities(ids);
    const handleChangeAdditionalQuestions = (ids: string[]) => setAdditionalQuestions(ids);
    const handleChangeDetailQuestions = (ids: string[]) => setDetailQuestions(ids);

    const openQuestionChoices = selectedQuestions
        .filter((q) => q.question.details.type === 'Open')
        .map((q) => ({ id: String(q.question.id), label: q.question.shortcut }));

    const singleQuestionChoices = selectedQuestions
        .filter((q) => q.question.details.type === 'Single')
        .map((q) => ({ id: String(q.question.id), label: q.question.shortcut }));

    const satisfactionQuestion = selectedQuestions.find((q) => String(q.question.id) === satisfaction);
    const satisfactionModalitiesChoices = satisfactionQuestion
        ? satisfactionQuestion.question.details.responses.map((r) => ({ id: String(r.id), label: r.maincaption }))
        : [];
    const positiveModalitiesChoices = satisfactionModalitiesChoices.filter((c) => !satisfactionNegativeModalities.includes(c.id));
    const negativeModalitiesChoices = satisfactionModalitiesChoices.filter((c) => !satisfactionPositiveModalities.includes(c.id));

    const configuratedHotAlerts: ConfiguratedHotAlerts = {
        openedQuestion: openedQuestion ? Number(openedQuestion) : undefined,
        firstName: firstName ? Number(firstName) : undefined,
        lastName: lastName ? Number(lastName) : undefined,
        phone: phone ? Number(phone) : undefined,
        mail: mail ? Number(mail) : undefined,
        satisfaction: satisfaction ? {
            questionId: Number(satisfaction),
            type: KPI_TYPE.SATISFACTION,
            positiveModalityIds: satisfactionPositiveModalities.map(Number),
            negativeModalityIds: satisfactionNegativeModalities.map(Number),
        } : undefined,
        additionalQuestion: additionalQuestions.map(Number),
        detailQuestions: detailQuestions.map(Number),
        statuses: statusConfigs,
    };

    useEffect(() => {
        onConfigureHotAlerts(configuratedHotAlerts);
    }, [openedQuestion, firstName, lastName, phone, mail, satisfaction, satisfactionPositiveModalities, satisfactionNegativeModalities, additionalQuestions, detailQuestions, statusConfigs]);

    const alreadySelectedIds = [openedQuestion, firstName, lastName, phone, mail, satisfaction].filter(Boolean) as string[];
    const additionalQuestionChoices = selectedQuestions
        .filter((q) => !alreadySelectedIds.includes(String(q.question.id)))
        .map((q) => ({ id: String(q.question.id), label: q.question.shortcut }));
    const detailQuestionChoices = selectedQuestions
        .map((q) => ({ id: String(q.question.id), label: q.question.shortcut }));

    const formGroups: FormGroup[] = [
        {
            id: 'openedQuestion',
            label: t('tabs.createSurvey.step5Content.opendedQuestion'),
            placeholder: t('tabs.createSurvey.step5Content.opendedQuestionPlaceholder'),
            value: openedQuestion,
            onChange: handleChangeOpenedQuestion,
            type: FormTypeEnum.SELECT_LIST,
            choices: openQuestionChoices,
            display: true,
            column: 1,
            isOptional: true,
        },
        {
            id: 'firstName',
            label: t('tabs.createSurvey.step5Content.firstName'),
            placeholder: t('tabs.createSurvey.step5Content.firstNamePlaceholder'),
            value: firstName,
            onChange: handleChangeFirstName,
            type: FormTypeEnum.SELECT_LIST,
            choices: openQuestionChoices,
            display: true,
            column: 1,
            isOptional: true,
        },
        {
            id: 'lastName',
            label: t('tabs.createSurvey.step5Content.lastName'),
            placeholder: t('tabs.createSurvey.step5Content.lastNamePlaceholder'),
            value: lastName,
            onChange: handleChangeLastName,
            type: FormTypeEnum.SELECT_LIST,
            choices: openQuestionChoices,
            display: true,
            column: 1,
            isOptional: true,
        },
        {
            id: 'phone',
            label: t('tabs.createSurvey.step5Content.phone'),
            placeholder: t('tabs.createSurvey.step5Content.phonePlaceholder'),
            value: phone,
            onChange: handleChangePhone,
            type: FormTypeEnum.SELECT_LIST,
            choices: openQuestionChoices,
            display: true,
            column: 1,
            isOptional: true,
        },
        {
            id: 'mail',
            label: t('tabs.createSurvey.step5Content.mail'),
            placeholder: t('tabs.createSurvey.step5Content.mailPlaceholder'),
            value: mail,
            onChange: handleChangeMail,
            type: FormTypeEnum.SELECT_LIST,
            choices: openQuestionChoices,
            display: true,
            column: 2,
            isOptional: true,
        },
        {
            id: 'satisfaction',
            label: t('tabs.createSurvey.step5Content.satisfaction'),
            placeholder: t('tabs.createSurvey.step5Content.satisfactionPlaceholder'),
            value: satisfaction,
            onChange: handleChangeSatisfaction,
            type: FormTypeEnum.SELECT_LIST,
            choices: singleQuestionChoices,
            display: true,
            column: 2,
            isOptional: true,
        },
        {
            id: 'satisfactionPositiveModalities',
            label: t('tabs.createSurvey.step5Content.satisfactionPositive'),
            placeholder: t('tabs.createSurvey.step5Content.satisfactionPositivePlaceholder'),
            value: satisfactionPositiveModalities,
            onChange: handleChangeSatisfactionPositiveModalities,
            type: FormTypeEnum.MULTI_SELECT_LIST,
            choices: positiveModalitiesChoices,
            display: satisfaction !== undefined && satisfactionModalitiesChoices.length > 0,
            column: 2,
            isOptional: false,
        },
        {
            id: 'satisfactionNegativeModalities',
            label: t('tabs.createSurvey.step5Content.satisfactionNegative'),
            placeholder: t('tabs.createSurvey.step5Content.satisfactionNegativePlaceholder'),
            value: satisfactionNegativeModalities,
            onChange: handleChangeSatisfactionNegativeModalities,
            type: FormTypeEnum.MULTI_SELECT_LIST,
            choices: negativeModalitiesChoices,
            display: satisfaction !== undefined && satisfactionModalitiesChoices.length > 0,
            column: 2,
            isOptional: false,
        },
        {
            id: 'additionalQuestion',
            label: t('tabs.createSurvey.step5Content.additionalQuestion'),
            placeholder: t('tabs.createSurvey.step5Content.additionalQuestionPlaceholder'),
            value: additionalQuestions,
            onChange: handleChangeAdditionalQuestions,
            type: FormTypeEnum.MULTI_SELECT_LIST,
            choices: additionalQuestionChoices,
            display: true,
            column: 3,
            isOptional: true,
        },
        {
            id: 'detailQuestions',
            label: t('tabs.createSurvey.step5Content.detailQuestions'),
            placeholder: t('tabs.createSurvey.step5Content.detailQuestionsPlaceholder'),
            value: detailQuestions,
            onChange: handleChangeDetailQuestions,
            type: FormTypeEnum.MULTI_SELECT_LIST,
            choices: detailQuestionChoices,
            display: true,
            column: 3,
            isOptional: true,
        },
    ];

    return (
        <>
            <Form formGroups={formGroups} id="#step5" title={t('tabs.createSurvey.step5')} />
            <HotAlertStatusEditor onChange={setStatusConfigs} statuses={statusConfigs} />
            <RequiredFieldsNote />
            <StepConfigureFooterStyled>
                <SimpleActionButton handleClickConfirm={() => handleNextStep('#step5')} labelConfirm={t('form.nextStep')} />
            </StepConfigureFooterStyled>
        </>
    );
};

export default StepConfigureHotAlerts;
